import { ArrowRight, Check, Quote, CalendarCheck } from "lucide-react";
import ConceptHeader from "./ConceptHeader";
import { ConceptFooter } from "./LayoutServicePro";

/**
 * "Editorial Calm" layout — soft, light, magazine-feel.
 * Used by health/care concepts (dental, chiropractic).
 * Distinct features: split image-first hero, "Meet the doctor" intro,
 * services with imagery, large testimonial quote, insurance band.
 */
export default function LayoutEditorialCalm({ c }) {
  const t = c.theme;
  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.fontBody, minHeight: "100vh" }}>
      <ConceptHeader industry={c.industry} />

      {/* Faux site nav */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 flex items-center justify-between">
        <p style={{ fontFamily: t.fontDisplay, fontSize: 26 }}>{c.name}</p>
        <nav className="hidden md:flex items-center gap-7 text-[13px]" style={{ color: t.muted }}>
          <span>Services</span><span>New Patients</span><span>About</span><span>Contact</span>
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium"
                style={{ background: t.accent, color: "#fff" }}>
            {c.hero.ctaPrimary}
          </span>
        </nav>
      </div>

      {/* HERO — split image left, copy right */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 pb-20 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative">
          <div className="aspect-[4/5] rounded-2xl overflow-hidden"
               style={{
                 backgroundColor: t.accentSoft,
                 backgroundImage: `url(${c.images.hero})`,
                 backgroundSize: "cover",
                 backgroundPosition: "center",
                 backgroundRepeat: "no-repeat",
               }}/>
          <div className="absolute -bottom-6 -right-6 hidden md:block p-6 rounded-2xl border bg-white"
               style={{ borderColor: t.muted + "33", boxShadow: "0 18px 48px -20px rgba(0,0,0,0.12)" }}>
            <div className="flex items-center gap-3">
              <CalendarCheck size={20} style={{ color: t.accent }}/>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>Next available</p>
                <p style={{ fontFamily: t.fontDisplay, fontSize: 20, color: t.text }}>Tomorrow · 9:30 AM</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6">
          <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>{c.hero.eyebrow}</p>
          <h1 className="mt-6 leading-[1.05]"
              style={{ fontFamily: t.fontDisplay, fontSize: "clamp(40px, 5vw, 68px)", fontWeight: 500 }}>
            {c.hero.title}
          </h1>
          <p className="mt-7 text-lg leading-relaxed max-w-md" style={{ color: t.muted }}>{c.hero.sub}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-[15px]"
                  style={{ background: t.accent, color: "#fff" }}>
              {c.hero.ctaPrimary} <ArrowRight size={15}/>
            </span>
            <span className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-[15px] border"
                  style={{ borderColor: t.muted, color: t.text }}>
              {c.hero.ctaSecondary}
            </span>
          </div>
        </div>
      </section>

      {/* MEET THE DOCTOR */}
      {c.doctor && (
        <section className="py-20 md:py-24" style={{ background: t.surface }}>
          <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <div className="aspect-square rounded-2xl overflow-hidden"
                   style={{ background: `url(${c.images.portrait || c.images.secondary}) center/cover` }}/>
            </div>
            <div className="lg:col-span-8">
              <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Meet your provider</p>
              <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(28px, 3.6vw, 44px)", fontWeight: 500 }}>
                {c.doctor.name}
              </h2>
              <p className="mt-1 text-[14px] uppercase tracking-[0.18em]" style={{ color: t.muted }}>{c.doctor.title}</p>
              <p className="mt-6 text-lg leading-relaxed" style={{ color: t.muted }}>{c.doctor.bio}</p>
            </div>
          </div>
        </section>
      )}

      {/* SERVICES — paired with secondary photo */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Care we provide</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 500 }}>
              Thoughtful care for every patient.
            </h2>
            <div className="mt-8 aspect-[4/3] rounded-2xl overflow-hidden"
                 style={{ background: `url(${c.images.tertiary || c.images.secondary}) center/cover` }}/>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {c.services.map((s, i) => (
              <div key={s.title} className="p-6 rounded-2xl border flex items-start gap-5"
                   style={{ background: t.surface, borderColor: t.muted + "22" }}>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-mono shrink-0"
                      style={{ background: t.accent + "20", color: t.accent }}>0{i+1}</span>
                <div>
                  <h3 style={{ fontFamily: t.fontDisplay, fontSize: 22 }}>{s.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed" style={{ color: t.muted }}>{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US strip */}
      <section className="py-14 border-y" style={{ background: t.surface, borderColor: t.muted + "22" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {c.valueProps.map((v) => (
            <div key={v} className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                    style={{ background: t.accent, color: "#fff" }}>
                <Check size={14}/>
              </span>
              <span className="text-[15px]">{v}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL — large quote */}
      <section className="py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Quote size={36} className="mx-auto" style={{ color: t.accent }}/>
          <p className="mt-8 leading-tight"
             style={{ fontFamily: t.fontDisplay, fontSize: "clamp(28px, 3.8vw, 48px)", fontWeight: 500 }}>
            "{c.testimonial.quote}"
          </p>
          <p className="mt-8 text-[14px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>— {c.testimonial.author}</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24" style={{ background: t.surface }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Welcoming new patients</p>
          <h2 className="mt-4 leading-tight"
              style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 500 }}>
            {c.finalCta}.
          </h2>
          <div className="mt-10 flex justify-center">
            <span className="inline-flex items-center gap-2 px-9 py-4 rounded-full font-medium text-[15px]"
                  style={{ background: t.accent, color: "#fff" }}>
              {c.hero.ctaPrimary} <ArrowRight size={16}/>
            </span>
          </div>
        </div>
      </section>

      <ConceptFooter accent={t.accent} muted={t.muted} bg={t.bg}/>
    </div>
  );
}
